import { computed, ComputedRef, Ref, ref } from "vue";
import { useTmapExpectedArrivalTimes } from "./useTmapExpectedArrivalTimes";
import { ArrivalTimeOptionCode, ArrivalTimeOptionCodeType } from "@/types";
import { IArrivalTime } from "@/domain/entities/lineInfo/arrivalTime.types";
import {
  IArrivalTimeOption,
  IBusLocation,
  ITurn,
} from "@/domain/entities/lineInfo";
import { IResultData } from "@/domain/entities/shared/result";
import { useArrivalTimeOptions } from "./useArrivalTimeOptions";
import { useArrivalTimes } from "./useArrivalTimes";
import dayjs from "dayjs";

interface ArrivalHandlerParams {
  busLocations: Ref<IBusLocation[]>;
  selectedBus: Ref<IBusLocation | undefined>;
  selectedTurn: ComputedRef<ITurn | undefined>;
  isInsideShuttle: ComputedRef<boolean>;
}

interface ArrivalHandlerReturn {
  arrivalTimes: Ref<IArrivalTime[]>;
  selectedArrivalTimeOption: Ref<{
    name: string;
    type: ArrivalTimeOptionCodeType;
  }>;
  updateSelectedArrivalTimeOption: (option: IArrivalTimeOption) => void;
  fetchArrivalTimes: (
    option: IArrivalTimeOption
  ) => Promise<IResultData<IArrivalTime[]>>;
}

export const useArrivalTimesHandler = ({
  busLocations,
  selectedBus,
  selectedTurn,
  isInsideShuttle,
}: ArrivalHandlerParams): ArrivalHandlerReturn => {
  const arrivalTimes = ref<IArrivalTime[]>([]);

  const arrivalTimesTable = computed(() => ({
    [ArrivalTimeOptionCode.STANDARD]: fetchStandardArrivalTimesUseCase,
    [ArrivalTimeOptionCode.TMAP]: fetchTmapArrivalTimesUseCase,
    [ArrivalTimeOptionCode.AVG]: fetchAvgArrivalTimesUseCase,
  }));

  const { selectedArrivalTimeOption, updateSelectedArrivalTimeOption } =
    useArrivalTimeOptions();

  const { fetchTmapExpectationArrivalTimes } = useTmapExpectedArrivalTimes(
    selectedBus.value?.busStopEvents.length ?? 0
  );

  const { getArrivalTimes: getAvgArrivalTimes } = useArrivalTimes(
    ArrivalTimeOptionCode.AVG
  );

  const fetchArrivalTimes = async (
    option: IArrivalTimeOption
  ): Promise<IResultData<IArrivalTime[]>> => {
    if (option.type === ArrivalTimeOptionCode.PAST) {
      return { isSuccess: true, data: [] };
    }

    const arrivalTimeUseCase = arrivalTimesTable.value[option.type];

    const result = await arrivalTimeUseCase(option);

    updateArrivalTimes(result.data ?? []);
    return result;
  };

  /** TMAP도착시각 */
  const fetchTmapArrivalTimesUseCase = async (option: IArrivalTimeOption) => {
    updateSelectedArrivalTimeOption(option);
    return await fetchTmapExpectationArrivalTimes(
      busLocations.value,
      selectedBus.value?.allocUid ?? "",
      isInsideShuttle.value
    );
  };

  /** 목표도착시각 */
  const fetchStandardArrivalTimesUseCase = async (
    option: IArrivalTimeOption
  ) => {
    updateSelectedArrivalTimeOption(option);
    return { isSuccess: true, data: [] };
  };

  /** 평균도착시각 */
  const fetchAvgArrivalTimesUseCase = async (option: IArrivalTimeOption) => {
    const selectedBusOrder =
      selectedBus.value?.busOrder ?? selectedTurn.value?.buses?.[0]?.busOrder;

    const selectedLineTurnUid =
      selectedBus.value?.lineTurnUid ?? selectedTurn.value?.id;

    if (selectedBusOrder === undefined || !selectedLineTurnUid) {
      return {
        isSuccess: false,
        data: [],
        errorMessage: "회차 또는 호차가 선택되지 않았습니다.",
      };
    }
    updateSelectedArrivalTimeOption(option);

    const selectedBusAllocUid =
      selectedBus.value?.allocUid ?? selectedTurn.value?.buses?.[0]?.allocUid;

    const result = await getAvgArrivalTimes(
      selectedLineTurnUid,
      selectedBusOrder,
      dayjs().format("YYYY-MM-DD"),
      selectedBusAllocUid
    );

    return result;
  };

  const updateArrivalTimes = (times: IArrivalTime[]) => {
    arrivalTimes.value = times;
  };

  return {
    arrivalTimes,
    selectedArrivalTimeOption,
    updateSelectedArrivalTimeOption,
    fetchArrivalTimes,
  };
};
